import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canManageUsers, ROLE_LABELS } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { UserPlus, Trash2, AlertTriangle, KeyRound, Copy, RefreshCw, Eye, EyeOff, Check, ShieldCheck, Upload, Download, FileSpreadsheet, X, CheckCircle2, AlertCircle, UserX, UserCheck, Building2, Pencil, FileJson, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useBranches } from "@/hooks/useBranches";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [branchTarget, setBranchTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [migrationOpen, setMigrationOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleSuspend = async (u) => {
    const suspending = !u.is_suspended;
    const action = suspending ? "suspend" : "unsuspend";
    const verb = suspending
      ? `Suspend ${u.name}? They will be force-logged-out immediately and blocked from signing back in.`
      : `Lift suspension on ${u.name}? They will be able to sign in again.`;
    if (!window.confirm(verb)) return;
    try {
      await api.post(`/users/${u.id}/${action}`);
      toast.success(suspending ? "User suspended & signed out" : "Suspension lifted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!canManageUsers(user?.role)) {
    return <div className="text-slate-500">Access restricted to administrators.</div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">User Management</h1>
          <p className="text-slate-500 mt-1 text-sm">{users.length} users · 11 roles</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="roles-matrix-btn">
            <Link to="/roles-permissions">
              <ShieldCheck className="w-4 h-4 mr-2" /> Roles &amp; Permissions
            </Link>
          </Button>
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-upload-users-btn">
                <Upload className="w-4 h-4 mr-2" /> Bulk Upload
              </Button>
            </DialogTrigger>
            <BulkUploadDialog onDone={() => { setBulkOpen(false); load(); }} />
          </Dialog>
          <Dialog open={migrationOpen} onOpenChange={setMigrationOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="export-migration-btn" title="Rotate passwords + export a JSON payload for user migration">
                <FileJson className="w-4 h-4 mr-2" /> Export for Migration
              </Button>
            </DialogTrigger>
            <ExportMigrationDialog onClose={() => setMigrationOpen(false)} />
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="new-user-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <NewUserForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 ${u.is_suspended ? "bg-rose-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-semibold text-xs ${u.is_suspended ? "bg-slate-200 text-slate-400" : "bg-[#fcf0f3] text-[#7C1D3A]"}`}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className={`font-medium ${u.is_suspended ? "text-slate-400 line-through" : "text-slate-900"}`}>{u.name}</div>
                        {u.is_suspended && (
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 mt-0.5 flex items-center gap-1" data-testid={`suspended-badge-${u.id}`}>
                            <UserX className="w-3 h-3" /> Suspended
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1" data-testid={`user-roles-${u.id}`}>
                      <Badge variant="outline" className="border-[#f5c4d3] text-[#7C1D3A]">{ROLE_LABELS[u.role]}</Badge>
                      {(u.secondary_roles || []).map((r) => (
                        <Badge key={r} variant="outline" className="border-slate-300 text-slate-500" data-testid={`user-secondary-role-${u.id}-${r}`}>+ {ROLE_LABELS[r] || r}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.department || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.phone_number || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== user.id && (
                      <div className="flex items-center justify-end gap-1">
                        {u.role !== "admin" && (
                          <button
                            onClick={() => toggleSuspend(u)}
                            className={`p-1.5 rounded ${u.is_suspended
                              ? "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                              : "text-slate-500 hover:text-amber-700 hover:bg-amber-50"}`}
                            data-testid={u.is_suspended ? `unsuspend-user-${u.id}` : `suspend-user-${u.id}`}
                            title={u.is_suspended ? "Lift suspension" : "Suspend user"}
                          >
                            {u.is_suspended ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          </button>
                        )}
                        {u.role !== "admin" && (
                          <button
                            onClick={() => setBranchTarget(u)}
                            className="text-slate-500 hover:text-[#7C1D3A] p-1.5 rounded hover:bg-[#fcf0f3]"
                            data-testid={`edit-branches-${u.id}`}
                            title="Edit branch access"
                          >
                            <Building2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setEditTarget(u)}
                          className="text-slate-500 hover:text-[#7C1D3A] p-1.5 rounded hover:bg-[#fcf0f3]"
                          data-testid={`edit-user-${u.id}`}
                          title="Edit user"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setResetTarget(u)}
                          className="text-slate-500 hover:text-[#7C1D3A] p-1.5 rounded hover:bg-[#fcf0f3]"
                          data-testid={`reset-password-${u.id}`}
                          title="Reset password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => del(u)}
                          className="text-slate-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                          data-testid={`delete-user-${u.id}`}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <FactoryResetPanel />

      {resetTarget && (
        <ResetPasswordDialog
          target={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
      {branchTarget && (
        <EditBranchAccessDialog
          target={branchTarget}
          onClose={() => setBranchTarget(null)}
          onDone={() => { setBranchTarget(null); load(); }}
        />
      )}
      {editTarget && (
        <EditUserDialog
          target={editTarget}
          // Count of *other* active admin-tier users. If >0, the current admin
          // can be safely demoted since another admin/admin_addl still exists.
          otherAdminCount={users.filter(u => u.id !== editTarget.id && !u.is_suspended && (u.role === "admin" || u.role === "admin_addl")).length}
          onClose={() => setEditTarget(null)}
          onDone={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}


function EditUserDialog({ target, otherAdminCount = 0, onClose, onDone }) {
  const [name, setName] = useState(target.name || "");
  const [email, setEmail] = useState(target.email || "");
  const [role, setRole] = useState(target.role || "technician");
  const [department, setDepartment] = useState(target.department || "");
  const [phone, setPhone] = useState(target.phone_number || "");
  const [secondaryRoles, setSecondaryRoles] = useState(target.secondary_roles || []);
  const [busy, setBusy] = useState(false);
  const toggleSecondary = (r) =>
    setSecondaryRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  // Feb 2026 — the primary `admin` role can now be reassigned provided at
  // least one other admin-tier user (admin or admin_addl) is still active.
  // If this IS the last admin, the dropdown stays locked to prevent lockout.
  const isAdmin = target.role === "admin";
  const isLastAdmin = isAdmin && otherAdminCount === 0;

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (!email.trim()) return toast.error("Email is required");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        department: department.trim(),
        phone_number: phone.trim(),
      };
      if (!isLastAdmin && role !== target.role) payload.role = role;
      payload.secondary_roles = secondaryRoles.filter((r) => r !== role);
      await api.patch(`/users/${target.id}`, payload);
      toast.success(`${target.name} updated`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-user-email" />
            <p className="text-[10px] text-slate-500 mt-1">
              Changing email updates the sign-in identifier. Notify the user before saving.
            </p>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isLastAdmin}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isLastAdmin && (
              <p className="text-[10px] text-amber-700 mt-1" data-testid="edit-user-role-locked-note">
                This is the last active admin-tier account. Promote another user to Admin or Admin_addl first, then this role can be changed.
              </p>
            )}
            {isAdmin && !isLastAdmin && (
              <p className="text-[10px] text-slate-500 mt-1" data-testid="edit-user-role-handoff-note">
                {otherAdminCount} other admin-tier account{otherAdminCount === 1 ? " is" : "s are"} active, so this admin can be safely reassigned.
              </p>
            )}
          </div>
          <div>
            <Label>Additional Roles <span className="font-normal text-slate-400">(optional — grants the combined permissions of every selected role)</span></Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="edit-user-secondary-roles">
              {Object.entries(ROLE_LABELS).filter(([k]) => k !== role).map(([k, v]) => {
                const on = secondaryRoles.includes(k);
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => toggleSecondary(k)}
                    className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
                    data-testid={`edit-user-secondary-role-${k}`}
                  >
                    {on ? "✓ " : ""}{v}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="edit-user-department" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-user-phone" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-user-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-user-save">
            {busy ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditBranchAccessDialog({ target, onClose, onDone }) {
  const { branches } = useBranches();
  const [selected, setSelected] = useState(new Set(target.allowed_branch_ids || []));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${target.id}`, { allowed_branch_ids: Array.from(selected) });
      toast.success(`Branch access updated for ${target.name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-branch-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#7C1D3A]" /> Branch Access · {target.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Select the branches this user should see across Consumables and requisitions. Empty selection grants access to all branches (legacy behaviour).
          </p>
          <div className="flex flex-wrap gap-2">
            {branches.length === 0 && <div className="text-xs text-slate-400">No branches configured yet.</div>}
            {branches.map((b) => {
              const on = selected.has(b.id);
              return (
                <button
                  key={b.id}
                  onClick={() => toggle(b.id)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
                  data-testid={`edit-branch-${b.code}`}
                >
                  {on ? "✓ " : ""}{b.name}
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-branch-access-btn">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onClose }) {
  const [mode, setMode] = useState("generate"); // "generate" | "manual"
  const [manualPassword, setManualPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {temporary_password} on success
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (mode === "manual") {
      if (!manualPassword || manualPassword.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = mode === "manual" ? { new_password: manualPassword } : { length: 12 };
      const { data } = await api.post(`/users/${target.id}/reset-password`, payload);
      if (data.generated) {
        setResult(data);
      } else {
        toast.success(`Password reset for ${target.name}`);
        onClose();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#7C1D3A]" /> Reset password
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">User</div>
              <div className="font-medium text-slate-900 mt-0.5">{target.name}</div>
              <div className="text-xs font-mono text-slate-500">{target.email}</div>
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-2.5 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "generate"}
                  onChange={() => setMode("generate")}
                  className="mt-1"
                  data-testid="reset-mode-generate"
                />
                <div>
                  <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Generate a temporary password
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Recommended. A strong 12-character password is generated. Copy it once and share out-of-band.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2.5 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "manual"}
                  onChange={() => setMode("manual")}
                  className="mt-1"
                  data-testid="reset-mode-manual"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">Set a specific password</div>
                  <div className="text-xs text-slate-500 mt-0.5">Type the exact password you want to set (min 8 chars).</div>
                  {mode === "manual" && (
                    <div className="relative mt-2">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={manualPassword}
                        onChange={(e) => setManualPassword(e.target.value)}
                        minLength={8}
                        placeholder="New password"
                        className="pr-10"
                        data-testid="manual-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2.5">
              Any existing sessions for <span className="font-semibold">{target.name}</span> will be signed out immediately after reset.
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting || (mode === "manual" && manualPassword.length < 8)}
                className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
                data-testid="confirm-reset-btn"
              >
                {submitting ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reset-password-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800">
              Password reset for <span className="font-semibold">{target.name}</span>. Share the temporary password below with them out-of-band (WhatsApp / phone / in person). This screen will not show it again.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Temporary password</Label>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 font-mono text-lg tracking-wider bg-slate-900 text-emerald-300 px-4 py-3 rounded-md select-all" data-testid="temp-password-display">
                  {result.temporary_password}
                </div>
                <Button
                  variant="outline"
                  onClick={copy}
                  data-testid="copy-temp-password-btn"
                  className="shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Ask the user to sign in with this password and change it right away from the profile menu.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="close-reset-result-btn">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FactoryResetPanel() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [wipeUsers, setWipeUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reset = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/factory-reset", {
        password,
        confirm_phrase: phrase,
        wipe_users: wipeUsers,
      });
      const d = data.deleted || {};
      toast.success(
        `Factory reset complete · assets: ${d.assets ?? 0}, maintenance: ${d.maintenance_logs ?? 0}, notifications: ${d.notifications ?? 0}, users: ${d.users ?? 0}`
      );
      setOpen(false);
      setPassword(""); setPhrase(""); setWipeUsers(true);
      // Reload the page so all cached state is cleared.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="border-red-200" data-testid="danger-zone">
      <CardHeader className="border-b border-red-100 bg-red-50/50">
        <CardTitle className="font-heading text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-slate-600 max-w-xl">
            <div className="font-semibold text-slate-900 mb-1">Factory Reset</div>
            Permanently deletes every asset, maintenance log, notification and (optionally) every other user account.
            Your admin account will be preserved so you can log back in. This action <span className="font-semibold text-red-700">cannot</span> be undone.
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" data-testid="factory-reset-btn">
                <AlertTriangle className="w-4 h-4 mr-2" /> Factory Reset
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Confirm Factory Reset
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-800">
                  This will permanently delete all data. Type your password and the exact confirmation phrase to proceed.
                </div>
                <div>
                  <Label>Your admin password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label>Type: <span className="font-mono text-red-700">RESET ASSET IQ</span></Label>
                  <Input
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder="RESET ASSET IQ"
                    data-testid="reset-phrase-input"
                  />
                </div>
                <label className="flex items-center gap-2 text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wipeUsers}
                    onChange={(e) => setWipeUsers(e.target.checked)}
                    data-testid="reset-wipe-users"
                  />
                  Also delete all other user accounts (keeps yours)
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={reset}
                  disabled={submitting || !password || phrase !== "RESET ASSET IQ"}
                  data-testid="confirm-factory-reset-btn"
                >
                  {submitting ? "Wiping…" : "Wipe Everything"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}

function NewUserForm({ onDone }) {
  const { branches } = useBranches();
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "technician", department: "", phone_number: "",
    allowed_branch_ids: [], secondary_roles: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSecondary = (r) =>
    setForm((f) => ({
      ...f,
      secondary_roles: f.secondary_roles.includes(r)
        ? f.secondary_roles.filter((x) => x !== r)
        : [...f.secondary_roles, r],
    }));
  const toggleBranch = (bid) => {
    setForm((f) => {
      const s = new Set(f.allowed_branch_ids || []);
      if (s.has(bid)) s.delete(bid); else s.add(bid);
      return { ...f, allowed_branch_ids: Array.from(s) };
    });
  };
  const isAdmin = form.role === "admin";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, secondary_roles: form.secondary_roles.filter((r) => r !== form.role) };
      if (isAdmin) delete payload.allowed_branch_ids;  // admins auto-see all
      await api.post("/users", payload);
      toast.success("User created");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle className="font-heading">Add New User</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-user-name" /></div>
        <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-user-email" /></div>
        <div><Label>Password *</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="new-user-password" /></div>
        <div>
          <Label>Role *</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Additional Roles <span className="font-normal text-slate-400">(optional — e.g. Accounts Manager also acting as HR Manager)</span></Label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="new-user-secondary-roles">
            {Object.entries(ROLE_LABELS).filter(([k]) => k !== form.role).map(([k, v]) => {
              const on = form.secondary_roles.includes(k);
              return (
                <button
                  type="button"
                  key={k}
                  onClick={() => toggleSecondary(k)}
                  className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
                  data-testid={`new-user-secondary-role-${k}`}
                >
                  {on ? "✓ " : ""}{v}
                </button>
              );
            })}
          </div>
        </div>
        <div><Label>Department</Label><Input value={form.department} onChange={(e) => set("department", e.target.value)} /></div>
        <div>
          <Label>Phone (with country code, for SMS alerts)</Label>
          <Input value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} placeholder="+919876543210" data-testid="new-user-phone" />
        </div>
        <div>
          <Label>Allowed Branches {!isAdmin && <span className="text-red-500">*</span>}</Label>
          {isAdmin ? (
            <div className="text-xs text-slate-500 mt-1 italic" data-testid="new-user-branches-admin">Administrators automatically have access to all branches.</div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-1" data-testid="new-user-branches">
              {branches.length === 0 && <div className="text-xs text-slate-400">No branches configured yet.</div>}
              {branches.map((b) => {
                const on = (form.allowed_branch_ids || []).includes(b.id);
                return (
                  <button
                    type="button"
                    key={b.id}
                    onClick={() => toggleBranch(b.id)}
                    className={`px-3 py-1 rounded-full border text-xs font-medium transition ${on ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
                    data-testid={`new-user-branch-${b.code}`}
                  >
                    {on ? "✓ " : ""}{b.name}
                  </button>
                );
              })}
            </div>
          )}
          {!isAdmin && (
            <p className="text-[11px] text-slate-400 mt-1">User will only see consumables belonging to the selected branches. Leave empty to grant access to all (legacy behaviour).</p>
          )}
        </div>
        <DialogFooter>
          <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-user-btn">
            {submitting ? "Creating..." : "Create User"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/**
 * Bulk-upload users from a CSV or Excel file.
 * Server generates a temp password for any row with a blank password column,
 * and returns the plaintext once (right here) so the admin can hand it out.
 */

function ExportMigrationDialog({ onClose }) {
  const [role, setRole] = useState("gate_keeper");
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null); // {count, users:[...]}

  const runExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/users/export-for-migration?role=${encodeURIComponent(role)}&include_suspended=${includeSuspended}`,
      );
      setResult(data);
      // Trigger download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `users_migration_${role}_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} ${role} user(s) & rotated passwords`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="export-migration-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileJson className="w-5 h-5 text-[#7C1D3A]" /> Export Role for Migration
        </DialogTitle>
      </DialogHeader>

      {!result ? (
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed">
            <div className="font-semibold flex items-center gap-1.5 mb-1">
              <AlertTriangle className="w-3.5 h-3.5" /> This is a destructive migration action
            </div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>A fresh temporary password will be generated for every matching user.</li>
              <li>Their existing passwords will be <b>overwritten</b> (old ones stop working immediately).</li>
              <li>All their active sessions will be <b>signed out</b>.</li>
              <li>Each user will be forced to reset the temp password on next login.</li>
              <li>The JSON file will contain temp passwords in plaintext — treat it like a secret.</li>
            </ul>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase text-slate-600">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="export-migration-role" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).filter(([k]) => k !== "admin").map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">
              Admin is excluded from this flow to avoid accidental lockout.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeSuspended}
              onChange={(e) => setIncludeSuspended(e.target.checked)}
              data-testid="export-migration-include-suspended"
              className="w-4 h-4 accent-[#7C1D3A]"
            />
            Include suspended users
          </label>

          <label className="flex items-start gap-2 text-sm cursor-pointer rounded-md border border-slate-200 bg-slate-50 p-3">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="export-migration-confirm"
              className="w-4 h-4 mt-0.5 accent-[#7C1D3A]"
            />
            <span className="text-slate-700 leading-snug">
              I understand this will rotate passwords and sign out every <b>{ROLE_LABELS[role] || role}</b> user.
            </span>
          </label>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <div className="font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Exported {result.count} {ROLE_LABELS[role] || role} user(s)
            </div>
            <p className="text-xs mt-1">
              A JSON file has been downloaded to your computer. Hand each user their temp password out-of-band.
            </p>
          </div>
          <div className="max-h-56 overflow-auto rounded border border-slate-200 text-xs">
            <table className="w-full">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold">Email</th>
                  <th className="px-2 py-1.5 font-semibold">Name</th>
                  <th className="px-2 py-1.5 font-semibold">Temp password</th>
                </tr>
              </thead>
              <tbody>
                {result.users.map((u) => (
                  <tr key={u.email} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 truncate max-w-[180px]" title={u.email}>{u.email}</td>
                    <td className="px-2 py-1.5 truncate max-w-[120px]" title={u.name}>{u.name}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">{u.temp_password}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DialogFooter>
        {!result ? (
          <>
            <Button variant="outline" onClick={onClose} data-testid="export-migration-cancel">Cancel</Button>
            <Button
              onClick={runExport}
              disabled={!confirmed || busy}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="export-migration-run"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting…</> : <><Download className="w-4 h-4 mr-2" /> Rotate & Download</>}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="export-migration-done">
            Done
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

function BulkUploadDialog({ onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const downloadTemplate = async (format) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(
        `${api.defaults.baseURL}/users/bulk/template?format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_bulk_template.${format === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Please select a file first");
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const c = data.summary.created_count;
      if (c > 0) toast.success(`Created ${c} user${c > 1 ? "s" : ""}`);
      else toast.info("No new users created — check the errors below");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  const copyAll = () => {
    if (!result?.created?.length) return;
    const rows = result.created.map(
      (r) => `${r.email}\t${r.name}\t${r.role}\t${r.temp_password}`,
    );
    navigator.clipboard.writeText(
      "email\tname\trole\ttemp_password\n" + rows.join("\n"),
    );
    toast.success("Copied credentials to clipboard");
  };

  const downloadCredsCsv = () => {
    if (!result?.created?.length) return;
    const lines = ["email,name,role,department,temp_password"];
    result.created.forEach((r) => {
      lines.push(
        `${r.email},${r.name},${r.role},${r.department || ""},${r.temp_password}`,
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk_users_credentials_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <DialogContent className="max-w-3xl" data-testid="bulk-upload-dialog">
      <DialogHeader>
        <DialogTitle>Bulk Upload Users</DialogTitle>
      </DialogHeader>

      {!result ? (
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600">
            <div className="font-semibold text-slate-800 mb-1">How it works</div>
            <ol className="list-decimal ml-4 space-y-0.5 text-xs">
              <li>Download the template (Excel has a &quot;Valid Roles&quot; tab for reference)</li>
              <li>Fill in one row per user. Leave the <span className="font-mono">password</span> column BLANK to auto-generate a strong temp password.</li>
              <li>Upload — you&apos;ll see the generated passwords once (they can&apos;t be recovered later, so download or copy the list).</li>
              <li>Rows with existing emails are skipped; invalid rows are reported.</li>
            </ol>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("xlsx")} data-testid="download-xlsx-template-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Download Excel template
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("csv")} data-testid="download-csv-template-btn">
              <Download className="w-4 h-4 mr-1" /> Download CSV template
            </Button>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) setFile(f);
            }}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOver ? "border-[#7C1D3A] bg-[#fcf0f3]" : "border-slate-300 bg-slate-50"
            }`}
            data-testid="bulk-upload-dropzone"
          >
            <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-[#7C1D3A]" : "text-slate-400"}`} />
            <label className="cursor-pointer">
              <div className="font-medium text-slate-700 text-sm">
                {file ? file.name : "Drop CSV / XLSX here or click to browse"}
              </div>
              <div className="text-xs text-slate-500 mt-1">.csv, .xlsx supported</div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0])}
                data-testid="bulk-upload-file-input"
              />
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFile(null)} disabled={!file || uploading}>
              Clear
            </Button>
            <Button
              onClick={submit}
              disabled={!file || uploading}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="bulk-upload-submit-btn"
            >
              {uploading ? "Uploading..." : "Upload & Create Users"}
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <SummaryTile icon={<UserPlus className="w-4 h-4" />} label="Total rows" value={result.summary.total_rows} />
            <SummaryTile icon={<CheckCircle2 className="w-4 h-4" />} label="Created" value={result.summary.created_count} tint="emerald" />
            <SummaryTile icon={<AlertCircle className="w-4 h-4" />} label="Skipped" value={result.summary.skipped_count} tint="amber" />
            <SummaryTile icon={<X className="w-4 h-4" />} label="Errors" value={result.summary.error_count} tint="rose" />
          </div>

          {/* Created list with temp passwords */}
          {result.created.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-emerald-900 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> {result.created.length} user{result.created.length > 1 ? "s" : ""} created
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyAll}>
                    <Copy className="w-3 h-3 mr-1" /> Copy list
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadCredsCsv} data-testid="download-creds-btn">
                    <Download className="w-3 h-3 mr-1" /> Download credentials CSV
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-amber-800 bg-amber-100/60 border border-amber-200 rounded px-2 py-1 mb-2">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Temp passwords are shown ONCE. Copy or download now — they can&apos;t be retrieved later.
              </div>
              <div className="max-h-48 overflow-y-auto text-xs">
                <table className="w-full">
                  <thead className="text-slate-500 text-[10px] uppercase">
                    <tr className="text-left">
                      <th className="py-1">Email</th>
                      <th className="py-1">Name</th>
                      <th className="py-1">Role</th>
                      <th className="py-1 text-right">Temp Password</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-emerald-100">
                    {result.created.map((r) => (
                      <tr key={r.email}>
                        <td className="py-1.5 font-mono text-slate-800">{r.email}</td>
                        <td className="py-1.5">{r.name}</td>
                        <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{ROLE_LABELS[r.role] || r.role}</Badge></td>
                        <td className="py-1.5 text-right font-mono text-[#7C1D3A] font-semibold">
                          {r.temp_password}
                          {r.was_generated && <span className="ml-1 text-[9px] text-slate-400 uppercase">auto</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Skipped */}
          {result.skipped.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-md p-3">
              <div className="text-sm font-semibold text-amber-900 flex items-center gap-1 mb-1">
                <AlertCircle className="w-4 h-4" /> {result.skipped.length} skipped
              </div>
              <ul className="text-xs text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                {result.skipped.map((s, i) => (
                  <li key={i}>Row {s.row} · <span className="font-mono">{s.email}</span> — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Errors */}
          {result.errors.length > 0 && (
            <div className="border border-rose-200 bg-rose-50/40 rounded-md p-3">
              <div className="text-sm font-semibold text-rose-900 flex items-center gap-1 mb-1">
                <X className="w-4 h-4" /> {result.errors.length} error{result.errors.length > 1 ? "s" : ""}
              </div>
              <ul className="text-xs text-rose-900 space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((er, i) => (
                  <li key={i}>Row {er.row}{er.email ? ` (${er.email})` : ""}: {er.error}</li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setResult(null); setFile(null); }} data-testid="bulk-upload-another-btn">
              Upload another
            </Button>
            <Button onClick={onDone} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="bulk-upload-done-btn">
              Done
            </Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  );
}

function SummaryTile({ icon, label, value, tint = "slate" }) {
  const tints = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`${tints[tint]} rounded-md p-2`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80 flex items-center justify-center gap-1">{icon}{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5">{value}</div>
    </div>
  );
}

